/**
 * Admin Analytics console — a first-party "Google Analytics".
 *
 *   - LIVE strip: who is on the site right now, what page, for how long
 *   - Overview: KPIs, traffic timeline, top pages, device/browser/OS/geo/
 *     referrer/landing breakdowns
 *   - Visitors: paginated per-visitor table → full drill-down modal with every
 *     session and page-by-page timeline
 *
 * Data comes from /api/admin/analytics/* (live | overview | visitors |
 * visitor/:id). Live + overview auto-refresh while the tab is visible.
 */
import { useEffect, useRef, useState } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { formatDate } from '../../utils/helpers';
import UsLiveMap from './UsLiveMap';
import WorldLiveMap from './WorldLiveMap';
import { Icon } from './AdminIcons';

// ─── formatting helpers ──────────────────────────────────────────────────────
const dur = (ms) => {
  if (!ms || ms < 1000) return `${ms ? Math.round(ms / 1000) : 0}s`;
  const s = Math.round(ms / 1000);
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  if (h) return `${h}h ${m % 60}m`;
  if (m) return `${m}m ${s % 60}s`;
  return `${s}s`;
};
const secs = (s) => dur((s || 0) * 1000);
const num = (n) => (n == null ? '0' : Number(n).toLocaleString());
const money = (cents) => '$' + ((Number(cents) || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Maps a device type to an AdminIcons name (closest available line-icon fit).
const DEVICE_ICON = { desktop: 'meters', mobile: 'orders', tablet: 'media', bot: 'settings' };
// Renders the device icon inline; falls back to nothing for unknown types.
const DeviceIcon = ({ device, className = 'w-4 h-4 inline-block align-text-bottom text-gray-400' }) =>
  DEVICE_ICON[device] ? <Icon name={DEVICE_ICON[device]} className={className} /> : null;
const RANGES = [
  { id: 'today', label: 'Today' },
  { id: '24h', label: '24h' },
  { id: '7d', label: '7 days' },
  { id: '30d', label: '30 days' },
  { id: '90d', label: '90 days' },
  { id: 'all', label: 'All time' },
];

// Short page label from a path (keeps it readable in tables).
const shortPath = (p) => {
  if (!p) return '/';
  const clean = p.split('?')[0];
  return clean.length > 42 ? clean.slice(0, 41) + '…' : clean;
};

// ─── tiny presentational atoms ───────────────────────────────────────────────
function Card({ label, value, sub, accent }) {
  return (
    <div className={'rounded-xl border p-4 shadow-sm ' + (accent ? 'bg-tea-50 border-tea-200' : 'bg-white')}>
      <div className="text-xs text-gray-500 mb-1">{label}</div>
      <div className={'text-2xl font-bold tabular-nums ' + (accent ? 'text-tea-700' : 'text-gray-900')}>{value}</div>
      {sub != null && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
    </div>
  );
}

function BarList({ title, items, render, accent = 'bg-tea-500', emptyLabel = 'No data yet' }) {
  const max = Math.max(1, ...items.map(i => i.value));
  return (
    <div className="bg-white rounded-xl border p-5 shadow-sm">
      <h3 className="text-sm font-bold text-gray-700 mb-3">{title}</h3>
      {items.length === 0 ? (
        <div className="text-sm text-gray-400 py-4 text-center">{emptyLabel}</div>
      ) : (
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={i} className="relative">
              <div className="flex items-center justify-between text-sm relative z-10 px-2 py-1">
                <span className="truncate text-gray-700" title={it.label}>{render ? render(it) : it.label}</span>
                <span className="tabular-nums font-medium text-gray-600 ml-3 flex-shrink-0">{num(it.value)}{it.suffix || ''}</span>
              </div>
              <div className={'absolute inset-y-0 left-0 rounded ' + accent + ' opacity-15'}
                   style={{ width: `${(it.value / max) * 100}%` }} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Pageviews-over-time bars.
function Timeline({ series }) {
  if (!series || series.length === 0) {
    return <div className="text-sm text-gray-400 py-8 text-center">No traffic in this range yet</div>;
  }
  const max = Math.max(1, ...series.map(s => Number(s.pageviews)));
  const showEvery = Math.ceil(series.length / 12);
  return (
    <div>
      <div className="flex items-end gap-1 h-40">
        {series.map((s, i) => {
          const h = Math.max(2, (Number(s.pageviews) / max) * 100);
          return (
            <div key={i} className="flex-1 flex flex-col justify-end group relative" title={`${s.bucket} — ${s.pageviews} views, ${s.visitors} visitors`}>
              <div className="w-full bg-tea-500 rounded-t hover:bg-tea-600 transition-colors" style={{ height: `${h}%` }} />
              <div className="absolute -top-7 left-1/2 -translate-x-1/2 hidden group-hover:block bg-gray-900 text-white text-[10px] rounded px-1.5 py-0.5 whitespace-nowrap z-20">
                {num(s.pageviews)} views
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex gap-1 mt-1">
        {series.map((s, i) => (
          <div key={i} className="flex-1 text-[9px] text-gray-400 text-center truncate">
            {i % showEvery === 0 ? s.bucket.replace(/^\d{4}-/, '').replace(' ', ' ') : ''}
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  LIVE strip
// ═══════════════════════════════════════════════════════════════════════════
function LivePanel() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = () => api.get('/admin/analytics/live')
      .then(r => { if (alive) setData(r.data); })
      .catch(() => {});
    load();
    const id = setInterval(() => { if (document.visibilityState === 'visible') load(); }, 5000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const count = data?.count || 0;

  return (
    <div className="bg-white rounded-xl border shadow-sm mb-6 overflow-hidden">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50">
        <div className="flex items-center gap-3">
          <span className="relative flex h-3 w-3">
            <span className={'absolute inline-flex h-full w-full rounded-full opacity-75 ' + (count ? 'bg-green-400 animate-ping' : 'bg-gray-300')} />
            <span className={'relative inline-flex rounded-full h-3 w-3 ' + (count ? 'bg-green-500' : 'bg-gray-400')} />
          </span>
          <span className="text-lg font-bold text-gray-900">{count}</span>
          <span className="text-sm text-gray-500">{count === 1 ? 'visitor' : 'visitors'} on the site right now</span>
        </div>
        <span className="text-gray-400 text-sm">{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div className="border-t">
          {count === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-gray-400">Nobody is browsing right now.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Who</th>
                    <th className="text-left px-4 py-2 font-medium">Current page</th>
                    <th className="text-left px-4 py-2 font-medium">Device</th>
                    <th className="text-left px-4 py-2 font-medium">Location</th>
                    <th className="text-right px-4 py-2 font-medium">On page</th>
                    <th className="text-right px-4 py-2 font-medium">Session</th>
                    <th className="text-right px-4 py-2 font-medium">Views</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.sessions.map(s => (
                    <tr key={s.sessionId} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5">
                        {s.name ? (
                          <span className="font-medium text-gray-800">{s.name}</span>
                        ) : (
                          <span className="text-gray-400">Anonymous</span>
                        )}
                        {s.email && <div className="text-xs text-gray-400">{s.email}</div>}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="text-gray-700" title={s.path}>{shortPath(s.path)}</span>
                      </td>
                      <td className="px-4 py-2.5 text-gray-600">
                        <span className="inline-flex items-center gap-1.5"><DeviceIcon device={s.device} />{s.browser} · {s.os}</span>
                      </td>
                      <td className="px-4 py-2.5 text-gray-600">
                        {s.region ? `${s.region}, ` : ''}{s.country || '—'}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{secs(s.onPageSeconds)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{secs(s.sessionSeconds)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{s.pageViews}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  OVERVIEW
// ═══════════════════════════════════════════════════════════════════════════
function Overview({ range }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapView, setMapView] = useState('world');   // 'world' | 'us'

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const load = () => api.get('/admin/analytics/overview', { params: { range } })
      .then(r => { if (alive) { setData(r.data); setLoading(false); } })
      .catch(() => { if (alive) { setLoading(false); toast.error('Failed to load analytics'); } });
    load();
    const id = setInterval(() => { if (document.visibilityState === 'visible') load(); }, 30000);
    return () => { alive = false; clearInterval(id); };
  }, [range]);

  if (loading && !data) return <div className="py-12 text-center text-gray-400">Loading analytics…</div>;
  if (!data) return null;

  const k = data.kpis;
  return (
    <div className="space-y-6">
      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Card label="Pageviews" value={num(k.pageviews)} accent />
        <Card label="Unique visitors" value={num(k.visitors)} sub={`${num(k.newVisitors)} new · ${num(k.returningVisitors)} returning`} />
        <Card label="Sessions" value={num(k.sessions)} sub={`${k.pageviewsPerSession} pages/session`} />
        <Card label="Avg. time on page" value={dur(k.avgTimeOnPageMs)} />
        <Card label="Avg. session" value={dur(k.avgSessionMs)} />
        <Card label="Bounce rate" value={`${k.bounceRate}%`} sub={`${num(k.loggedInVisitors)} logged-in`} />
      </div>

      {/* Traffic timeline */}
      <div className="bg-white rounded-xl border p-5 shadow-sm">
        <h3 className="text-sm font-bold text-gray-700 mb-4">Traffic over time</h3>
        <Timeline series={data.timeseries} />
      </div>

      {/* Live visitor map — toggle between worldwide and US-state detail */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">Visitor map</span>
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            {[['world', 'World'], ['us', 'US']].map(([id, label]) => (
              <button key={id} onClick={() => setMapView(id)}
                className={'px-3 py-1 rounded-md text-xs font-medium transition-colors ' +
                  (mapView === id ? 'bg-white text-tea-700 shadow-sm' : 'text-gray-500 hover:text-gray-700')}>
                {label}
              </button>
            ))}
          </div>
        </div>
        {mapView === 'world'
          ? <WorldLiveMap countries={data.worldCountries || []} />
          : <UsLiveMap usStates={data.usStates || []} />}
      </div>

      {/* Top pages */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <h3 className="text-sm font-bold text-gray-700 px-5 pt-5 pb-3">Top pages — time spent &amp; engagement</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="text-left px-5 py-2 font-medium">Page</th>
                <th className="text-right px-4 py-2 font-medium">Views</th>
                <th className="text-right px-4 py-2 font-medium">Visitors</th>
                <th className="text-right px-4 py-2 font-medium">Avg. time</th>
                <th className="text-right px-5 py-2 font-medium">Avg. scroll</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.topPages.map((p, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-5 py-2.5 text-gray-700" title={p.path}>{shortPath(p.path)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{num(p.views)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{num(p.visitors)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{dur(Math.round(p.avg_time_ms))}</td>
                  <td className="px-5 py-2.5 text-right tabular-nums">{Math.round(p.avg_scroll)}%</td>
                </tr>
              ))}
              {data.topPages.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-8 text-center text-gray-400">No pageviews in this range yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Breakdowns */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        <BarList title="Devices" accent="bg-blue-500"
          items={data.devices.map(d => ({ label: d.label, value: Number(d.views) }))}
          render={it => <span className="inline-flex items-center gap-1.5"><DeviceIcon device={it.label} />{it.label}</span>} />
        <BarList title="Browsers" accent="bg-indigo-500"
          items={data.browsers.map(d => ({ label: d.label, value: Number(d.views) }))} />
        <BarList title="Operating systems" accent="bg-purple-500"
          items={data.oses.map(d => ({ label: d.label, value: Number(d.views) }))} />
        <BarList title="Countries / regions" accent="bg-emerald-500"
          items={data.countries.map(d => ({ label: d.label, value: Number(d.views) }))} />
        <BarList title="Languages" accent="bg-amber-500"
          items={data.languages.map(d => ({ label: d.label, value: Number(d.views) }))} />
        <BarList title="Traffic sources (referrers)" accent="bg-rose-500"
          items={data.referrers.map(d => ({ label: d.label, value: Number(d.sessions), suffix: ' sess' }))} />
        <BarList title="Landing pages" accent="bg-teal-500"
          items={data.landingPages.map(d => ({ label: shortPath(d.path), value: Number(d.entries) }))} />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  VISITORS table + drill-down
// ═══════════════════════════════════════════════════════════════════════════
function VisitorModal({ visitorId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/admin/analytics/visitor/${visitorId}`)
      .then(r => setData(r.data))
      .catch(() => { toast.error('Failed to load visitor'); onClose(); });
  }, [visitorId, onClose]);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full my-8" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-gray-900">
                {data?.user ? data.user.name : 'Anonymous visitor'}
              </h2>
              {data?.live && (
                <span className="flex items-center gap-1 text-xs font-medium text-green-600">
                  <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" /> live now
                </span>
              )}
            </div>
            <div className="text-xs text-gray-400 font-mono">{visitorId}</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>

        {!data ? (
          <div className="py-16 text-center text-gray-400">Loading…</div>
        ) : (
          <div className="p-6 space-y-5">
            {/* Identity */}
            {data.user && (
              <div className="bg-tea-50 border border-tea-200 rounded-xl p-4 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-gray-500">Email:</span> {data.user.email}</div>
                  <div><span className="text-gray-500">Role:</span> {data.user.role}</div>
                  <div><span className="text-gray-500">Member since:</span> {formatDate(data.user.joined)}</div>
                </div>
              </div>
            )}

            {/* Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card label="Pageviews" value={num(data.summary.pageviews)} />
              <Card label="Sessions" value={num(data.summary.sessions)} />
              <Card label="Total time" value={dur(data.summary.totalDurationMs)} />
              <Card label="Avg / page" value={dur(data.summary.avgTimeOnPageMs)} />
            </div>

            {/* Environment */}
            <div className="bg-gray-50 rounded-xl p-4 text-sm grid grid-cols-2 md:grid-cols-3 gap-2">
              <div><span className="text-gray-500">Device:</span> <span className="inline-flex items-center gap-1.5"><DeviceIcon device={data.latest.device} />{data.latest.device}</span></div>
              <div><span className="text-gray-500">Browser:</span> {data.latest.browser}</div>
              <div><span className="text-gray-500">OS:</span> {data.latest.os}</div>
              <div><span className="text-gray-500">Location:</span> {data.latest.region ? data.latest.region + ', ' : ''}{data.latest.country || '—'}</div>
              <div><span className="text-gray-500">Timezone:</span> {data.latest.timezone || '—'}</div>
              <div><span className="text-gray-500">Language:</span> {data.latest.language || '—'}</div>
              <div><span className="text-gray-500">Screen:</span> {data.latest.screen || '—'}</div>
              <div><span className="text-gray-500">Viewport:</span> {data.latest.viewport || '—'}</div>
              <div><span className="text-gray-500">IP:</span> <span className="font-mono text-xs">{data.latest.ip || '—'}</span></div>
              {(data.latest.utm?.source || data.latest.utm?.campaign) && (
                <div className="col-span-2 md:col-span-3"><span className="text-gray-500">Campaign:</span> {data.latest.utm.source} / {data.latest.utm.medium} / {data.latest.utm.campaign}</div>
              )}
              <div className="col-span-2 md:col-span-3 text-xs text-gray-400">
                First seen {formatDate(data.summary.firstSeen)} · Last seen {formatDate(data.summary.lastSeen)}
              </div>
            </div>

            {/* Sessions */}
            <div>
              <h3 className="text-sm font-bold text-gray-700 mb-2">Sessions &amp; page journeys</h3>
              <div className="space-y-3">
                {data.sessions.map((s, i) => (
                  <div key={s.sessionId} className="border rounded-xl overflow-hidden">
                    <div className="bg-gray-50 px-4 py-2 flex items-center justify-between text-xs text-gray-600">
                      <span className="inline-flex items-center gap-1.5">{formatDate(s.startedAt)} · <DeviceIcon device={s.device} />{s.browser}/{s.os}{s.country ? ' · ' + s.country : ''}</span>
                      <span className="tabular-nums">{s.pageViews} pages · {dur(s.durationMs)}</span>
                    </div>
                    <ol className="divide-y">
                      {s.pages.map((pg, j) => (
                        <li key={j} className="px-4 py-2 flex items-center justify-between text-sm">
                          <div className="min-w-0">
                            <span className="text-gray-700" title={pg.path}>{shortPath(pg.path)}</span>
                            {pg.title && <span className="text-gray-400 text-xs ml-2 truncate">{pg.title}</span>}
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0 text-xs text-gray-500 tabular-nums">
                            <span>{dur(pg.durationMs)}</span>
                            <span>{pg.scrollPct}% scroll</span>
                            <span>{pg.clicks} clicks</span>
                          </div>
                        </li>
                      ))}
                    </ol>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Visitors({ range }) {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);

  useEffect(() => { setPage(1); }, [range]);
  useEffect(() => {
    let alive = true;
    api.get('/admin/analytics/visitors', { params: { range, page } })
      .then(r => { if (alive) setData(r.data); })
      .catch(() => toast.error('Failed to load visitors'));
    return () => { alive = false; };
  }, [range, page]);

  if (!data) return <div className="py-12 text-center text-gray-400">Loading visitors…</div>;

  return (
    <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b flex items-center justify-between">
        <h3 className="text-sm font-bold text-gray-700">All visitors</h3>
        <span className="text-xs text-gray-400">{num(data.pagination.total)} total · click a row for full detail</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
            <tr>
              <th className="text-left px-5 py-2 font-medium">Visitor</th>
              <th className="text-left px-4 py-2 font-medium">Device / browser</th>
              <th className="text-left px-4 py-2 font-medium">Location</th>
              <th className="text-left px-4 py-2 font-medium">Last page</th>
              <th className="text-right px-4 py-2 font-medium">Sessions</th>
              <th className="text-right px-4 py-2 font-medium">Views</th>
              <th className="text-right px-4 py-2 font-medium">Time</th>
              <th className="text-right px-5 py-2 font-medium">Last seen</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {data.visitors.map(v => (
              <tr key={v.visitorId} className="hover:bg-tea-50 cursor-pointer" onClick={() => setSelected(v.visitorId)}>
                <td className="px-5 py-2.5">
                  {v.user ? (
                    <>
                      <div className="font-medium text-gray-800">{v.user.name || v.user.email}</div>
                      <div className="text-xs text-gray-400">{v.user.email}</div>
                    </>
                  ) : (
                    <span className="text-gray-400 font-mono text-xs">{v.visitorId.slice(0, 8)}…</span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-gray-600"><span className="inline-flex items-center gap-1.5"><DeviceIcon device={v.device} />{v.browser} · {v.os}</span></td>
                <td className="px-4 py-2.5 text-gray-600">{v.region ? v.region + ', ' : ''}{v.country || '—'}</td>
                <td className="px-4 py-2.5 text-gray-600" title={v.lastPath}>{shortPath(v.lastPath)}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{v.sessions}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{v.pageviews}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{dur(v.totalDurationMs)}</td>
                <td className="px-5 py-2.5 text-right text-xs text-gray-500">{formatDate(v.lastSeen)}</td>
              </tr>
            ))}
            {data.visitors.length === 0 && (
              <tr><td colSpan={8} className="px-5 py-8 text-center text-gray-400">No visitors in this range yet</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data.pagination.pages > 1 && (
        <div className="flex items-center justify-between px-5 py-3 border-t text-sm">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
            className="px-3 py-1 rounded border disabled:opacity-40 hover:bg-gray-50">Previous</button>
          <span className="text-gray-500">Page {page} of {data.pagination.pages}</span>
          <button disabled={page >= data.pagination.pages} onClick={() => setPage(p => p + 1)}
            className="px-3 py-1 rounded border disabled:opacity-40 hover:bg-gray-50">Next</button>
        </div>
      )}

      {selected && <VisitorModal visitorId={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  Page shell
// ═══════════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════════
//  BLOG / PRESS engagement
// ═══════════════════════════════════════════════════════════════════════════
function Blog({ range }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get('/admin/analytics/blog', { params: { range } })
      .then(res => { if (active) setData(res.data); })
      .catch(() => { if (active) toast.error('Failed to load blog analytics'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [range]);

  if (loading) return <div className="py-12 text-center text-gray-400">Loading press analytics…</div>;
  if (!data) return <div className="py-12 text-center text-gray-400">No blog analytics yet.</div>;

  const t = data.totals;
  const top = data.posts.filter(p => p.views > 0).slice(0, 8);

  return (
    <div className="space-y-6">
      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card label="Article views" value={num(t.views)} sub={`${num(t.feedViews)} on the feed index`} accent />
        <Card label="Unique readers" value={num(t.readers)} sub={`${num(t.feedReaders)} browsed the feed`} />
        <Card label="Avg time on article" value={dur(t.avgTimeMs)} sub={`${t.avgScroll}% avg scroll depth`} />
        <Card label="Posts" value={num(t.published)} sub={`${num(t.drafts)} drafts · ${num(t.posts)} total`} />
      </div>

      {/* Press traffic over time */}
      <div className="bg-white rounded-xl border p-5 shadow-sm">
        <h3 className="text-sm font-bold text-gray-700 mb-3 inline-flex items-center gap-2"><Icon name="forecast" className="w-4 h-4 text-tea-500" />Press traffic over time</h3>
        <Timeline series={data.timeseries} />
      </div>

      {/* Most-read bar list */}
      <BarList
        title={<span className="inline-flex items-center gap-2"><Icon name="flame" className="w-4 h-4 text-tea-500" />Most-read articles (this range)</span>}
        items={top.map(p => ({ label: p.title, value: p.views }))}
        emptyLabel="No article reads in this range yet"
      />

      {/* Per-post table */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b">
          <h3 className="text-sm font-bold text-gray-700">All posts — engagement</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b text-gray-500">
              <tr>
                {['Article', 'Status', 'Views', 'Readers', 'Avg time', 'Scroll', 'Lifetime'].map(h => (
                  <th key={h} className="px-4 py-2.5 text-left font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.posts.length === 0 ? (
                <tr><td colSpan={7} className="px-5 py-8 text-center text-gray-400">No posts yet</td></tr>
              ) : data.posts.map(p => (
                <tr key={p.id} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="px-4 py-2.5">
                    <a href={`/press/${p.slug}`} target="_blank" rel="noopener noreferrer"
                      className="font-medium text-gray-800 hover:text-tea-700">{p.title}</a>
                    {p.category && <span className="ml-2 text-[10px] text-gray-400">{p.category}</span>}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={'text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ' +
                      (p.status === 'published' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700')}>
                      {p.status}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 tabular-nums font-semibold text-gray-800">{num(p.views)}</td>
                  <td className="px-4 py-2.5 tabular-nums text-gray-600">{num(p.readers)}</td>
                  <td className="px-4 py-2.5 tabular-nums text-gray-600">{dur(p.avgTimeMs)}</td>
                  <td className="px-4 py-2.5 tabular-nums text-gray-600">{p.avgScroll}%</td>
                  <td className="px-4 py-2.5 tabular-nums text-gray-400">{num(p.lifetimeViews)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  ABANDONED CARTS
// ═══════════════════════════════════════════════════════════════════════════
const CART_STATUS = {
  active:    { label: 'Active',    cls: 'bg-blue-100 text-blue-700' },
  abandoned: { label: 'Abandoned', cls: 'bg-amber-100 text-amber-700' },
  recovered: { label: 'Recovered', cls: 'bg-emerald-100 text-emerald-700' },
  converted: { label: 'Converted', cls: 'bg-gray-100 text-gray-600' },
  empty:     { label: 'Empty',     cls: 'bg-gray-100 text-gray-400' },
};

function CartStatusBadge({ status }) {
  const s = CART_STATUS[status] || CART_STATUS.empty;
  return <span className={'text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ' + s.cls}>{s.label}</span>;
}

function Carts({ range }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const load = () => api.get('/admin/analytics/carts', { params: { range } })
      .then(res => { if (active) { setData(res.data); setLoading(false); } })
      .catch(() => { if (active) { setLoading(false); toast.error('Failed to load cart analytics'); } });
    load();
    const id = setInterval(() => { if (document.visibilityState === 'visible') load(); }, 30000);
    return () => { active = false; clearInterval(id); };
  }, [range]);

  if (loading && !data) return <div className="py-12 text-center text-gray-400">Loading cart analytics…</div>;
  if (!data) return null;

  const k = data.kpis;
  return (
    <div className="space-y-6">
      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Card label="Active carts" value={num(k.activeCarts)} />
        <Card label="Abandoned carts" value={num(k.abandonedCarts)} accent />
        <Card label="Abandoned value" value={money(k.abandonedValueInCents)} sub="left in idle carts" />
        <Card label="Recovery emails sent" value={num(k.emailsSent)} />
        <Card label="Recovered" value={num(k.recoveredCount)} sub={`${money(k.recoveredValueInCents)} reclaimed`} />
        <Card label="Recovery rate" value={`${k.recoveryRate}%`} sub={`${k.conversionRate}% cart conversion`} />
      </div>

      {/* Carts created vs recovered over time */}
      {data.timeseries && data.timeseries.length > 0 && (
        <div className="bg-white rounded-xl border p-5 shadow-sm">
          <h3 className="text-sm font-bold text-gray-700 mb-4">Carts over time</h3>
          <Timeline series={data.timeseries.map(t => ({ bucket: t.bucket, pageviews: t.created, visitors: t.recovered }))} />
          <div className="text-xs text-gray-400 mt-2">Bars: carts created · hover for recovered count</div>
        </div>
      )}

      {/* Cart table */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <h3 className="text-sm font-bold text-gray-700">Carts</h3>
          <span className="text-xs text-gray-400">Most recent activity first · up to 50 shown</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="text-left px-5 py-2 font-medium">Shopper</th>
                <th className="text-left px-4 py-2 font-medium">Items</th>
                <th className="text-right px-4 py-2 font-medium">Value</th>
                <th className="text-left px-4 py-2 font-medium">Status</th>
                <th className="text-left px-4 py-2 font-medium">Recovery email</th>
                <th className="text-right px-5 py-2 font-medium">Last activity</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.carts.map(c => (
                <tr key={c.id} className="hover:bg-gray-50 align-top">
                  <td className="px-5 py-2.5">
                    {c.customer ? (
                      <>
                        <div className="font-medium text-gray-800">{c.customer.name || c.customer.email}</div>
                        <div className="text-xs text-gray-400">{c.customer.email}</div>
                      </>
                    ) : c.guestEmail ? (
                      <>
                        <div className="font-medium text-gray-800">Guest</div>
                        <div className="text-xs text-gray-400">{c.guestEmail}</div>
                      </>
                    ) : (
                      <span className="text-gray-400 font-mono text-xs" title={c.visitorId}>Anonymous · {c.visitorId.slice(0, 8)}…</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-gray-600">
                    <span title={(c.itemsSummary || []).join('\n')}>
                      {c.itemCount} item{c.itemCount === 1 ? '' : 's'}
                    </span>
                    {c.itemsSummary && c.itemsSummary.length > 0 && (
                      <div className="text-xs text-gray-400 truncate max-w-[220px]" title={c.itemsSummary.join('\n')}>
                        {c.itemsSummary[0]}{c.itemsSummary.length > 1 ? ` +${c.itemsSummary.length - 1} more` : ''}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums font-medium text-gray-800">{money(c.subtotalInCents)}</td>
                  <td className="px-4 py-2.5"><CartStatusBadge status={c.status} /></td>
                  <td className="px-4 py-2.5 text-xs text-gray-500">
                    {c.firstEmailSentAt ? (
                      <div className="space-y-0.5">
                        <div>1st · {formatDate(c.firstEmailSentAt)}</div>
                        {c.secondEmailSentAt && <div>2nd · {formatDate(c.secondEmailSentAt)}</div>}
                      </div>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-5 py-2.5 text-right text-xs text-gray-500">{formatDate(c.lastActivityAt)}</td>
                </tr>
              ))}
              {data.carts.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-8 text-center text-gray-400">No carts in this range yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function AnalyticsPage() {
  const [range, setRange] = useState('24h');
  const [tab, setTab] = useState('overview');

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-3xl font-bold inline-flex items-center gap-2"><Icon name="analytics" className="w-7 h-7 text-tea-500" />Analytics</h1>
        <div className="flex flex-wrap gap-1 bg-gray-100 rounded-lg p-1">
          {RANGES.map(r => (
            <button key={r.id} onClick={() => setRange(r.id)}
              className={'px-3 py-1.5 rounded-md text-sm font-medium transition-colors ' +
                (range === r.id ? 'bg-white text-tea-700 shadow-sm' : 'text-gray-500 hover:text-gray-700')}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Live is always on top */}
      <LivePanel />

      {/* Sub-tabs */}
      <div className="flex gap-1 border-b mb-6">
        {[['overview', 'Overview'], ['visitors', 'Visitors'], ['carts', 'Carts'], ['press', 'Press']].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ' +
              (tab === id ? 'border-tea-600 text-tea-700' : 'border-transparent text-gray-500 hover:text-gray-700')}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'overview' ? <Overview range={range} />
        : tab === 'press' ? <Blog range={range} />
        : tab === 'carts' ? <Carts range={range} />
        : <Visitors range={range} />}
    </div>
  );
}
